/**
 * Datos del recorrido de demostracion.
 *
 * Todo lo que se ve en el recorrido es ficticio y vive aqui, en el cliente: la
 * demostracion no requiere sesion, no llama a la API y no toca ninguna base.
 *
 * Lo que NO es ficticio es la aritmetica. Cada importe pasa por las mismas
 * funciones de @abarrotes/shared que usa la caja de verdad (lineTotalCents,
 * buildScaleBarcode, parseScaleBarcode, marginPct...), de modo que los numeros
 * que el recorrido presume son el motor de produccion corriendo sobre datos de
 * ejemplo, no capturas pintadas a mano. Y las cifras cuadran entre si a
 * proposito: los gastos del corte son la suma de los gastos capturados, el
 * total de las sucursales es la suma de sus tres renglones. Un recorrido que
 * presume control del dinero no puede traer numeros que no cierran.
 */

import {
  buildScaleBarcode,
  changeCents,
  divRoundHalfUp,
  lineTotalCents,
  marginPct,
  MILLI,
  parseScaleBarcode,
  sumCents,
  type ScaleBarcodeConfig,
} from '@abarrotes/shared';

// ---------------------------------------------------------------------------
// La caja
// ---------------------------------------------------------------------------

export interface DemoTicketLine {
  name: string;
  brand?: string;
  /** Codigo que "dispara" el lector en la escena. Sin codigo = boton rapido. */
  code?: string;
  unitPriceCents: number;
  qtyMilli: number;
  unit: string;
}

/**
 * Tres renglones, no cuatro: dos escaneos y uno por boton rapido cuentan la
 * historia completa, y el cuarto era justo el que hacia que la escena ya no
 * cupiera en el alto que deja el navegador de un telefono real.
 */
export const TICKET_LINES: readonly DemoTicketLine[] = [
  {
    name: 'Coca-Cola 600 ml',
    brand: 'Coca-Cola',
    code: '7501055300075',
    unitPriceCents: 1_900,
    qtyMilli: 2 * MILLI,
    unit: 'pz',
  },
  {
    name: 'Sabritas Sal 45 g',
    brand: 'Sabritas',
    code: '7500478008148',
    unitPriceCents: 1_850,
    qtyMilli: MILLI,
    unit: 'pz',
  },
  // Sin codigo: entra por boton rapido, como el hielo o el jitomate reales.
  { name: 'Bolsa de hielo 5 kg', unitPriceCents: 2_500, qtyMilli: MILLI, unit: 'pz' },
];

export function demoLineTotal(line: DemoTicketLine): number {
  return lineTotalCents(line.unitPriceCents, line.qtyMilli);
}

/** Subtotal del ticket con las primeras `upTo` lineas visibles. */
export function ticketTotalCents(upTo: number): number {
  return sumCents(TICKET_LINES.slice(0, upTo).map(demoLineTotal));
}

export const TICKET_TOTAL_CENTS = ticketTotalCents(TICKET_LINES.length);
export const RECEIVED_CENTS = 20_000;
export const CHANGE_CENTS = changeCents(RECEIVED_CENTS, TICKET_TOTAL_CENTS);

/** Botones de acceso rapido de la escena; el primero es el que se "presiona". */
export const QUICK_BUTTONS: readonly string[] = [
  'Bolsa de hielo 5 kg',
  'Jitomate',
  'Cilantro',
  'Huevo suelto',
];

// ---------------------------------------------------------------------------
// Granel y bascula
// ---------------------------------------------------------------------------

export const SCALE_CONFIG: ScaleBarcodeConfig = {
  mode: 'weight',
  prefix: '25',
  itemDigits: 5,
  valueDigits: 5,
};

export const GUAJILLO = {
  itemCode: '00042',
  name: 'Chile guajillo',
  pricePerKgCents: 18_000,
};

/** La etiqueta que imprimiria la bascula: 1.750 kg del articulo 00042. */
export const SCALE_CODE = buildScaleBarcode(GUAJILLO.itemCode, 1_750, SCALE_CONFIG);

/** El parser de produccion leyendo esa etiqueta. */
const scaleRead = parseScaleBarcode(SCALE_CODE, SCALE_CONFIG);
export const SCALE_QTY_MILLI = scaleRead?.qtyMilli ?? 0;
export const SCALE_LINE_CENTS = lineTotalCents(GUAJILLO.pricePerKgCents, SCALE_QTY_MILLI);

/** El codigo partido en sus tramos, para señalarlos uno por uno en la escena. */
export const SCALE_SEGMENTS = {
  prefix: SCALE_CODE.slice(0, 2),
  item: SCALE_CODE.slice(2, 7),
  value: SCALE_CODE.slice(7, 12),
  check: SCALE_CODE.slice(12),
};

// ---------------------------------------------------------------------------
// Panel en vivo
// ---------------------------------------------------------------------------

export const DAY_HOURS: readonly { hour: string; cents: number }[] = [
  { hour: '8:00', cents: 21_400 },
  { hour: '9:00', cents: 38_600 },
  { hour: '10:00', cents: 45_200 },
  { hour: '11:00', cents: 52_800 },
  { hour: '12:00', cents: 61_400 },
  { hour: '13:00', cents: 78_300 },
  { hour: '14:00', cents: 92_600 },
  { hour: '15:00', cents: 71_200 },
  { hour: '16:00', cents: 58_900 },
  { hour: '17:00', cents: 64_100 },
  { hour: '18:00', cents: 72_400 },
  { hour: '19:00', cents: 83_900 },
  { hour: '20:00', cents: 66_200 },
  { hour: '21:00', cents: 36_200 },
];

/** Indice de la hora pico dentro de `DAY_HOURS`. */
export const PEAK_HOUR_INDEX = DAY_HOURS.reduce(
  (best, entry, index) => (entry.cents > (DAY_HOURS[best]?.cents ?? 0) ? index : best),
  0,
);
export const PEAK_HOUR_LABEL = DAY_HOURS[PEAK_HOUR_INDEX]?.hour ?? '';

export const DAY_TOTAL_CENTS = sumCents(DAY_HOURS.map((h) => h.cents));
export const DAY_TICKETS = 117;
export const DAY_PROFIT_CENTS = 218_000;

/** Lo que mas se vende hoy, ordenado por dinero — como lo enseña el panel. */
export const TOP_PRODUCTS: readonly { name: string; qtyLabel: string; cents: number }[] = [
  { name: 'Chile guajillo', qtyLabel: '6.250 kg', cents: lineTotalCents(18_000, 6_250) },
  { name: 'Coca-Cola 600 ml', qtyLabel: '38 pz', cents: lineTotalCents(1_900, 38 * MILLI) },
  { name: 'Huevo blanco', qtyLabel: '11.500 kg', cents: lineTotalCents(4_800, 11_500) },
];

// ---------------------------------------------------------------------------
// Inventario
// ---------------------------------------------------------------------------

export type DemoStockState = 'out' | 'low' | 'negative' | 'ok';

export interface DemoInventoryRow {
  name: string;
  stockLabel: string;
  state: DemoStockState;
  note: string;
}

export const INVENTORY_ROWS: readonly DemoInventoryRow[] = [
  {
    name: 'Aceite vegetal 1 L',
    stockLabel: '0 pz',
    state: 'out',
    note: 'Cada hora sin el es venta que se va.',
  },
  {
    name: 'Coca-Cola 600 ml',
    stockLabel: '5 pz',
    state: 'low',
    note: 'Su punto de reorden son 12.',
  },
  {
    name: 'Huevo blanco',
    stockLabel: '-0.5 kg',
    state: 'negative',
    note: 'Se vendio sin existencia: el cliente se fue con su huevo.',
  },
  {
    name: 'Arroz a granel',
    stockLabel: '59 kg',
    state: 'ok',
    note: '17 dias de cobertura al ritmo actual.',
  },
];

// ---------------------------------------------------------------------------
// Surtido
// ---------------------------------------------------------------------------

export const RESTOCK = {
  name: 'Coca-Cola 600 ml',
  brand: 'Coca-Cola',
  velocityLabel: '3.4 por dia',
  stockPieces: 5,
  coverLabel: '1.5 dias',
  leadLabel: '2 dias',
  suggestedPieces: 24,
  reason:
    'Vendes 3.4 al dia y te quedan 5: dia y medio. El proveedor tarda 2. Con 24 cubres la semana.',
};

export const RESTOCK_LOW = {
  name: 'Chicle Motita fresa',
  daysOfHistory: 6,
};

// ---------------------------------------------------------------------------
// Merma y margen real
// ---------------------------------------------------------------------------

const SHRINK_BOUGHT_MILLI = 10 * MILLI; // el costal: 10 kg
const SHRINK_SOLD_MILLI = 9_400; // lo que de verdad paso por la bascula
const SHRINK_COST_PER_KG_CENTS = 9_500;
const SHRINK_PRICE_PER_KG_CENTS = 18_000;

/** Costo real por kilo VENDIDO: la factura repartida entre los kilos que si salen. */
const SHRINK_REAL_COST_CENTS = divRoundHalfUp(
  SHRINK_COST_PER_KG_CENTS * SHRINK_BOUGHT_MILLI,
  SHRINK_SOLD_MILLI,
);

export const SHRINK = {
  boughtMilli: SHRINK_BOUGHT_MILLI,
  soldMilli: SHRINK_SOLD_MILLI,
  /** Lo que la deshidratacion explica (2.5 % mensual sobre el costal). */
  naturalMilli: 250,
  /** Lo que la deshidratacion NO explica. */
  adjustMilli: 350,
  costPerKgCents: SHRINK_COST_PER_KG_CENTS,
  pricePerKgCents: SHRINK_PRICE_PER_KG_CENTS,
  realCostPerKgCents: SHRINK_REAL_COST_CENTS,
  nominalPct: marginPct(SHRINK_PRICE_PER_KG_CENTS, SHRINK_COST_PER_KG_CENTS) ?? 0,
  realPct: marginPct(SHRINK_PRICE_PER_KG_CENTS, SHRINK_REAL_COST_CENTS) ?? 0,
};

// ---------------------------------------------------------------------------
// Credito
// ---------------------------------------------------------------------------

export const CREDIT_CUSTOMER = {
  name: 'Fonda Doña Mary',
  detail: 'Surte su cocina cada semana',
  limitCents: 300_000,
  openingBalanceCents: 124_000,
};

export const CREDIT_SALE_CENTS = 34_800;

/** Saldo resultante del cargo, como lo guarda el libro: renglon por renglon. */
export const BALANCE_AFTER_SALE = CREDIT_CUSTOMER.openingBalanceCents + CREDIT_SALE_CENTS;

export type DemoCollectionUrgency = 'vencido' | 'vence_hoy' | 'al_corriente';

export interface DemoAgendaEntry {
  name: string;
  detail: string;
  urgency: DemoCollectionUrgency;
  owedCents: number;
  /** Como paga, aprendido de su historial. */
  habitLabel: string;
  /** La explicacion redactada, como la escribe el motor de cobranza. */
  reason: string;
}

export const AGENDA: readonly DemoAgendaEntry[] = [
  {
    name: 'Ernesto Gatica',
    detail: 'Compra costales al mayoreo',
    urgency: 'vencido',
    owedCents: 234_000,
    habitLabel: 'Paga puntual',
    reason:
      'Se vencio hace 22 dias y el siempre paga a tiempo: se le paso. Una llamada lo resuelve.',
  },
  {
    name: 'Fonda Doña Mary',
    detail: 'Surte su cocina cada semana',
    urgency: 'vence_hoy',
    owedCents: BALANCE_AFTER_SALE,
    habitLabel: 'Liquida cada viernes',
    reason: 'Quedaron para hoy y nunca ha fallado. Hoy toca pasar a verla.',
  },
];

// ---------------------------------------------------------------------------
// Gastos y corte
// ---------------------------------------------------------------------------

export const EXPENSES_ROWS: readonly { label: string; category: string; cents: number }[] = [
  { label: 'Mercancia · abarrotero mayorista', category: 'Mercancia', cents: 98_000 },
  { label: 'Luz del local (CFE)', category: 'Servicios', cents: 43_000 },
  { label: 'Flete del mercado de abastos', category: 'Fletes', cents: 21_950 },
];

const CUT_FUND_CENTS = 80_000;
const CUT_CASH_SALES_CENTS = 708_400;
/** Los gastos del corte SON la suma de los capturados: las cifras cierran. */
const CUT_EXPENSES_CENTS = sumCents(EXPENSES_ROWS.map((e) => e.cents));
const CUT_EXPECTED_CENTS = sumCents([CUT_FUND_CENTS, CUT_CASH_SALES_CENTS]) - CUT_EXPENSES_CENTS;
const CUT_COUNTED_CENTS = 624_000;

export const CASH_CUT = {
  fundCents: CUT_FUND_CENTS,
  cashSalesCents: CUT_CASH_SALES_CENTS,
  expensesCents: CUT_EXPENSES_CENTS,
  expectedCents: CUT_EXPECTED_CENTS,
  countedCents: CUT_COUNTED_CENTS,
  /** Negativo: falto dinero. Se registra tal cual, no se fuerza a cero. */
  differenceCents: CUT_COUNTED_CENTS - CUT_EXPECTED_CENTS,
};

// ---------------------------------------------------------------------------
// Multisucursal
// ---------------------------------------------------------------------------

export const STORES_LIVE: readonly { name: string; cents: number; tickets: number }[] = [
  { name: 'Centro', cents: DAY_TOTAL_CENTS, tickets: DAY_TICKETS },
  { name: 'Norte', cents: 512_300, tickets: 74 },
  { name: 'Mercado', cents: 389_050, tickets: 51 },
];

export const STORES_TOTAL_CENTS = sumCents(STORES_LIVE.map((s) => s.cents));
export const STORES_TOTAL_TICKETS = STORES_LIVE.reduce((acc, s) => acc + s.tickets, 0);
